/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-pascal-case */
import { classNames } from '@commenda/utils';
import React from 'react';
import { Tooltip } from '../Tooltip/Tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from './_base';

interface IconProps {
  className: string;
}

type IconComponent = React.ComponentType<IconProps>;

export interface DropdownMenuItemProps {
  icon?: IconComponent;
  label: string;
  shortcut?: string | React.ReactNode;
  disabled?: boolean;
  subMenu?: DropdownMenuProps;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  iconClassName?: string;
  labelClassName?: string;
  className?: string;
}

interface DropdownMenuGroupProps {
  label?: string;
  items: DropdownMenuItemProps[];
}

interface DropdownMenuProps {
  groups: DropdownMenuGroupProps[];
}

export interface DropdownProps {
  menu: DropdownMenuProps;
  children: React.ReactNode;
  align?: 'center' | 'end' | 'start';
  side?: 'top' | 'bottom' | 'left' | 'right';
  sideOffset?: number;
  alignOffset?: number;
  contentClassName?: string;
  itemClassName?: string;
  labelClassName?: string;
  /** className applied to the header label shown above each labeled group. */
  groupLabelClassName?: string;
  /**
   * When true, the automatic separators that appear after labeled groups and
   * between adjacent groups are suppressed. Useful for designs where section
   * labels alone provide enough visual grouping.
   */
  hideSeparators?: boolean;
  /**
   * When true, suppress Radix's default behavior of returning focus to the
   * trigger after the menu closes. Avoids a stray focus ring on mouse-driven
   * close. Tab still focuses the trigger normally.
   */
  preventAutoFocusOnClose?: boolean;
  /**
   * Notifies callers when the menu opens / closes — lets the parent
   * highlight the row owning the trigger while the menu is open, etc.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Optional tooltip shown on hover/focus of the trigger. Composes via
   * Radix Slot — both `TooltipTrigger asChild` and `DropdownMenuTrigger
   * asChild` project onto the same single child, so consumers don't need
   * to hand-wire the primitives.
   */
  triggerTooltip?: React.ReactNode;
  /** Side for the trigger tooltip. Defaults to `top`. */
  triggerTooltipSide?: 'top' | 'right' | 'bottom' | 'left';
}

const Dropdown = ({
  menu,
  align = 'center',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 0,
  children,
  contentClassName = '',
  itemClassName = '',
  labelClassName = '',
  groupLabelClassName = '',
  hideSeparators = false,
  preventAutoFocusOnClose = false,
  onOpenChange,
  triggerTooltip,
  triggerTooltipSide = 'top',
}: DropdownProps) => {
  const handleItemClick = (
    event: React.MouseEvent<HTMLDivElement>,
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void,
    disabled = false
  ) => {
    event.stopPropagation();
    if (!disabled) onClick?.(event);
  };

  const renderSubDropdownMenu = (item: DropdownMenuItemProps) => {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          {item.icon && (
            <item.icon className="mr-2 h-4 w-4 text-fg-quaternary" />
          )}
          <span>{item.label}</span>
        </DropdownMenuSubTrigger>
        <DropdownMenuPortal>
          <DropdownMenuSubContent>
            {item.subMenu?.groups.map((group, groupIndex) => (
              <React.Fragment key={groupIndex}>
                {group.label && (
                  <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
                )}
                {group.label && <DropdownMenuSeparator />}
                <DropdownMenuGroup>
                  {group.items.map((_item, itemIndex) => {
                    return _item.subMenu ? (
                      renderSubDropdownMenu(_item)
                    ) : (
                      <DropdownMenuItem
                        key={itemIndex}
                        disabled={_item.disabled}
                        onClick={(event) =>
                          handleItemClick(event, _item.onClick, _item.disabled)
                        }
                      >
                        {_item.icon && (
                          <_item.icon
                            className={classNames(
                              'mr-2 h-4 w-4',
                              _item.iconClassName || ''
                            )}
                          />
                        )}
                        <span>{_item.label}</span>
                        {_item.shortcut &&
                          (typeof _item.shortcut === 'string' ? (
                            <DropdownMenuShortcut>
                              {_item.shortcut}
                            </DropdownMenuShortcut>
                          ) : (
                            _item.shortcut
                          ))}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuGroup>
                {groupIndex < (item.subMenu?.groups.length || 0) - 1 && (
                  <DropdownMenuSeparator />
                )}
              </React.Fragment>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuPortal>
      </DropdownMenuSub>
    );
  };

  // Composes Tooltip + DropdownMenu triggers onto the same child via two
  // layers of Radix `asChild` (Slot merges refs + handlers). Tooltip on the
  // outside so it owns hover/focus; Dropdown inside so it owns click/keyboard
  // open. Without this composition, wrapping the trigger in a Tooltip at the
  // call site breaks one or the other.
  const trigger = triggerTooltip ? (
    <Tooltip content={triggerTooltip} side={triggerTooltipSide} asChild>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
    </Tooltip>
  ) : (
    <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
  );

  const renderDropdownMenu = (_menu: DropdownMenuProps) => (
    <DropdownMenu onOpenChange={onOpenChange}>
      {trigger}
      <DropdownMenuContent
        className={classNames('w-56', contentClassName)}
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        onCloseAutoFocus={
          preventAutoFocusOnClose ? (e) => e.preventDefault() : undefined
        }
      >
        {_menu.groups.map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            {group.label && (
              <DropdownMenuLabel className={groupLabelClassName}>
                {group.label}
              </DropdownMenuLabel>
            )}
            {group.label && !hideSeparators && <DropdownMenuSeparator />}
            <DropdownMenuGroup>
              {group.items.map((item, itemIndex) => {
                return item.subMenu ? (
                  renderSubDropdownMenu(item)
                ) : (
                  <DropdownMenuItem
                    key={itemIndex}
                    disabled={item.disabled}
                    onClick={(event) =>
                      handleItemClick(event, item.onClick, item.disabled)
                    }
                    className={classNames(itemClassName, item.className || '')}
                  >
                    <div className="flex items-center flex-1">
                      {item.icon && (
                        <item.icon
                          className={classNames(
                            'mr-2 h-4 w-4 shrink-0',
                            item.iconClassName || ''
                          )}
                        />
                      )}
                      <span
                        className={classNames(
                          labelClassName,
                          item.labelClassName || ''
                        )}
                      >
                        {item.label}
                      </span>
                    </div>
                    <div>
                      {item.shortcut &&
                        (typeof item.shortcut === 'string' ? (
                          <DropdownMenuShortcut>
                            {item.shortcut}
                          </DropdownMenuShortcut>
                        ) : (
                          item.shortcut
                        ))}
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuGroup>
            {groupIndex < _menu.groups.length - 1 && !hideSeparators && (
              <DropdownMenuSeparator />
            )}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return renderDropdownMenu(menu);
};

export { Dropdown };
export type { DropdownMenuProps };
